import { useState } from "react";
import { Plus, CreditCard, Banknote, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePaymentMethods, PaymentMethod } from "@/hooks/usePaymentMethods";
import { PaymentMethodFormDialog } from "@/components/payment-methods/PaymentMethodFormDialog";
import { PaymentMethodList } from "@/components/payment-methods/PaymentMethodList";

export default function FormasPagamentoPage() {
  const {
    paymentMethods,
    isLoading,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,
    toggleActive,
  } = usePaymentMethods();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);

  const handleCreate = () => {
    setSelectedMethod(null);
    setDialogOpen(true);
  };

  const handleEdit = (method: PaymentMethod) => {
    setSelectedMethod(method);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    await deletePaymentMethod(id);
  };

  const handleToggleActive = async (id: string) => {
    await toggleActive(id);
  };

  const handleSubmit = async (data: any) => {
    if (selectedMethod) {
      await updatePaymentMethod(selectedMethod.id, data);
    } else {
      await createPaymentMethod(data);
    }
    setDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Formas de Pagamento</h1>
          <p className="text-muted-foreground">
            Gerencie as formas de pagamento aceitas pelo seu estabelecimento
          </p>
        </div>
        <Button onClick={handleCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Nova Forma de Pagamento
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Banknote className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Dinheiro</p>
              <p className="text-lg font-semibold">
                {paymentMethods.filter((m) => m.type === "cash" && m.is_active).length} ativo(s)
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Cartão</p>
              <p className="text-lg font-semibold">
                {paymentMethods.filter((m) => m.type === "card" && m.is_active).length} ativo(s)
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Smartphone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Carteira Digital</p>
              <p className="text-lg font-semibold">
                {paymentMethods.filter((m) => m.type === "digital_wallet" && m.is_active).length} ativo(s)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Methods List */}
      <PaymentMethodList
        paymentMethods={paymentMethods}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      {/* Form Dialog */}
      <PaymentMethodFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        paymentMethod={selectedMethod}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
